import { ClaimParentCard } from "@/components/parent/ClaimParentCard";
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { CreditCard, CheckCircle2, ArrowUpCircle } from "lucide-react";
import { toast } from "sonner";
import { listMyStudentTCs, listMyStudents } from "@/lib/api/parent-invites.functions";
import { listPlansForMyTc, subscribeMyChildPlan, upgradeStudentPlan } from "@/lib/api/parent-students.functions";
import { paymentMethodLabel, paymentMethodChannel } from "@/lib/payment-methods";
import { brToday } from "@/lib/tz";

export const Route = createFileRoute("/_authenticated/responsavel/planos")({
  head: () => ({
    meta: [
      { title: "Planos de Pagamento | FutBux" },
      { name: "description", content: "Acompanhe o plano ativo dos seus alunos e faça upgrade." },
      { property: "og:title", content: "Planos de Pagamento | FutBux" },
      { property: "og:description", content: "Acompanhe o plano ativo dos seus alunos e faça upgrade." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ParentPlansPage,
});

const ACTIVE_KEY = "futbux:activeParentTc";
const fmtBRL = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (d?: string | null) => (d ? new Date(d + "T00:00:00").toLocaleDateString("pt-BR") : "—");

function ParentPlansPage() {
  const navigate = useNavigate();
  const listTcs = useServerFn(listMyStudentTCs);
  const listStudentsFn = useServerFn(listMyStudents);
  const listPlansFn = useServerFn(listPlansForMyTc);
  const subscribe = useServerFn(subscribeMyChildPlan);
  const upgrade = useServerFn(upgradeStudentPlan);

  const [tcId, setTcId] = useState<string | null>(null);
  const [tcName, setTcName] = useState("");
  const [students, setStudents] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [parentName, setParentName] = useState<string>("");
  const [loading, setLoading] = useState(true);

  // subscribe (novo)
  const [pickPlan, setPickPlan] = useState<any | null>(null);
  const [studentId, setStudentId] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [saving, setSaving] = useState(false);

  // upgrade
  const [afterSubscribe, setAfterSubscribe] = useState<{ allowSelfEnroll: boolean } | null>(null);
  const [upgradeFor, setUpgradeFor] = useState<any | null>(null);
  const [upgradeTargetId, setUpgradeTargetId] = useState("");
  const [upgradeMethod, setUpgradeMethod] = useState("");

  const reload = async (activeId: string) => {
    const [pRes, sRes] = await Promise.all([
      listPlansFn({ data: { tcId: activeId } }),
      listStudentsFn(),
    ]);
    setPlans(pRes.plans ?? []);
    setStudents((sRes.students ?? []).filter((s: any) => s.training_center_id === activeId));
  };

  useEffect(() => {
    (async () => {
      const stored = (() => { try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; } })();
      const tcsRes = await listTcs();
      const tcs = tcsRes.tcs ?? [];
      if (tcs.length === 0) { setLoading(false); return; }
      const active = tcs.find((t: any) => t.training_center_id === stored) ?? tcs[0];
      if (tcs.length > 1 && !stored) { navigate({ to: "/selecionar-ct" }); return; }
      setTcId(active.training_center_id);
      setTcName(active.tc_name);
      try { localStorage.setItem(ACTIVE_KEY, active.training_center_id); } catch {}
      await reload(active.training_center_id);
      setLoading(false);
    })();
  }, []);

  const activeStudents = useMemo(
    () => students.filter((s) => s.status === "active" || s.status === "ativo" || !s.status),
    [students],
  );

  // Enrich students with plan info (join do próprio aluno ou catálogo do CT)
  const studentsWithPlan = useMemo(() => {
    const planById = new Map(plans.map((p) => [p.id, p]));
    return activeStudents.map((s: any) => ({
      ...s,
      _plan: s.plan_prices ?? (s.plan_price_id ? planById.get(s.plan_price_id) : null) ?? null,
    }));
  }, [activeStudents, plans]);


  const openPick = (plan: any) => {
    setPickPlan(plan);
    setStudentId(activeStudents[0]?.id ?? "");
    setPayMethod(plan.accepted_payment_methods?.[0] ?? "");
  };

  const openUpgrade = (student: any, planId?: string) => {
    setUpgradeFor(student);
    const higher = higherPlansFor(student);
    const chosen = higher.find((p: any) => p.id === planId) ?? higher[0];
    setUpgradeTargetId(chosen?.id ?? "");
    setUpgradeMethod(chosen?.accepted_payment_methods?.[0] ?? "");
  };

  const higherPlansFor = (student: any) =>
    plans.filter((p) => Number(p.price) > Number(student._plan?.price ?? 0));

  /** Migração: novo plano só começa no dia seguinte ao fim do contrato atual. */
  const migrationStartFor = (student: any) => {
    const today = brToday();
    const curEnd = student?.plan_end_date as string | undefined;
    if (curEnd && curEnd >= today) {
      const d = new Date(`${curEnd}T00:00:00`);
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    }
    return today;
  };

  const onConfirmSubscribe = async () => {
    if (!tcId || !pickPlan || !studentId || !payMethod) {
      toast.error("Selecione o aluno e a forma de pagamento.");
      return;
    }
    setSaving(true);
    try {
      const res = await subscribe({
        data: { tcId, studentId, planPriceId: pickPlan.id, paymentMethod: payMethod },
      });
      toast.success("Plano assinado! Vigência até " + fmtDate(res.end));
      setPickPlan(null);
      await reload(tcId);
      if (res.channel === "comprovante") {
        navigate({ to: `/pix/${studentId}` as any });
      } else {
        setAfterSubscribe({ allowSelfEnroll: (res as any).allowSelfEnroll === true });
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Falha ao assinar plano.");
    } finally {
      setSaving(false);
    }
  };

  const onConfirmUpgrade = async () => {
    if (!upgradeFor || !upgradeTargetId || !upgradeMethod) {
      toast.error("Selecione o novo plano e a forma de pagamento.");
      return;
    }
    setSaving(true);
    try {
      const res = await upgrade({
        data: { studentId: upgradeFor.id, newPlanPriceId: upgradeTargetId, paymentMethod: upgradeMethod },
      });
      toast.success(
        (res as any).migration
          ? `Migração confirmada! Nova vigência de ${fmtDate((res as any).start)} até ${fmtDate(res.end)}.`
          : "Upgrade realizado! Vigência até " + fmtDate(res.end),
      );
      setUpgradeFor(null);
      if (tcId) await reload(tcId);
      if (res.channel === "comprovante") navigate({ to: `/pix/${upgradeFor.id}` as any });
    } catch (e: any) {
      toast.error(e?.message ?? "Falha ao fazer upgrade.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Carregando…</div>;

  if (!tcId) {
    return <ClaimParentCard />;
  }


  const withoutPlan = studentsWithPlan.filter((s: any) => !s._plan);

  return (
    <div className="p-4 space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-primary" /> Planos de Pagamento
        </h1>
        <p className="text-sm text-muted-foreground">{tcName}</p>
      </div>

      {/* SEÇÃO 1 — Planos ativos por aluno */}
      <section>
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-500" /> Planos Ativos
        </h2>
        {studentsWithPlan.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
            Cadastre um aluno em <b>Meus Alunos</b> para assinar um plano.
          </CardContent></Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {studentsWithPlan.map((s: any) => {
              const p = s._plan;
              const higher = higherPlansFor(s);
              return (
                <Card key={s.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between gap-2">
                      <span className="truncate">{s.name}</span>
                      {p ? <Badge variant="default">Ativo</Badge> : <Badge variant="secondary">Sem plano</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {p ? (
                      <>
                        <div className="rounded-md bg-muted/40 p-3 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">{p.plan_type}</span>
                            <Badge variant="outline" className="capitalize">{p.validity}</Badge>
                          </div>
                          <div className="text-xl font-bold text-primary">{fmtBRL(Number(p.price ?? 0))}</div>
                          <div className="text-xs text-muted-foreground">
                            Vigência: {fmtDate(s.plan_start_date)} → {fmtDate(s.plan_end_date)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Forma: {s.payment_method ? paymentMethodLabel(s.payment_method) : "—"}
                          </div>
                        </div>
                        {(s.plan_contracts ?? [])
                          .filter((c: any) => c.status === "agendado")
                          .map((c: any) => (
                            <div key={c.id} className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-1">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold text-sm">{c.plan_type}</span>
                                <div className="flex gap-1">
                                  <Badge variant="outline" className="border-amber-500 text-amber-700">Agendado</Badge>
                                  <Badge variant={c.payment_status === "pago" ? "default" : "secondary"}>
                                    {c.payment_status === "pago" ? "Pago" : "Pagamento pendente"}
                                  </Badge>
                                </div>
                              </div>
                              <div className="text-lg font-bold text-primary">{fmtBRL(Number(c.amount ?? 0))}</div>
                              <div className="text-xs text-muted-foreground">
                                Vigência: {fmtDate(c.start_date)} → {fmtDate(c.end_date)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Forma: {c.payment_method ? paymentMethodLabel(c.payment_method) : "—"}
                              </div>
                            </div>
                          ))}
                        {higher.length === 0 ? (
                          <p className="text-xs text-muted-foreground rounded-md border p-2 text-center">
                            Você já está no plano mais alto disponível.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-muted-foreground">
                              Planos disponíveis para upgrade
                            </p>
                            {higher.map((up: any) => (
                              <div key={up.id} className="rounded-md border p-3 space-y-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium text-sm">{up.plan_type}</span>
                                  <Badge variant="outline" className="capitalize">{up.validity}</Badge>
                                </div>
                                <div className="text-lg font-bold text-primary">{fmtBRL(Number(up.price ?? 0))}</div>
                                <div className="text-xs text-emerald-700">
                                  + {fmtBRL(Number(up.price ?? 0) - Number(p.price ?? 0))} em relação ao plano atual
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Início da nova vigência: <b>{fmtDate(migrationStartFor(s))}</b> (após o término do contrato atual)
                                </div>
                                <Button
                                  size="sm"
                                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                                  onClick={() => openUpgrade(s, up.id)}
                                >
                                  <ArrowUpCircle className="w-4 h-4 mr-2" /> Fazer upgrade
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">Sem plano ativo. Escolha um abaixo para assinar.</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* SEÇÃO 2 — Planos disponíveis para assinar (novos) */}
      {plans.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold mb-3">Planos disponíveis</h2>
          {withoutPlan.length === 0 && (
            <p className="text-sm text-muted-foreground mb-3 rounded-md border p-3">
              Estes são os planos oferecidos pelo seu Centro de Treinamento. Cadastre um aluno em{" "}
              <b>Meus Alunos</b> para assinar.
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((p) => (
              <Card key={p.id} className="flex flex-col">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{p.plan_type}</span>
                    <Badge variant="secondary" className="capitalize">{p.validity}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col justify-between gap-3">
                  <div>
                    <div className="text-2xl font-bold text-primary">{fmtBRL(Number(p.price ?? 0))}</div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Formas: {(p.accepted_payment_methods ?? []).map(paymentMethodLabel).join(", ") || "—"}
                    </div>
                  </div>
                  <Button
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    disabled={withoutPlan.length === 0}
                    onClick={() => openPick(p)}
                  >
                    {withoutPlan.length === 0 ? "Cadastre um aluno" : "Assinar"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Modal ASSINAR */}
      <Dialog open={!!pickPlan} onOpenChange={(o) => !o && setPickPlan(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assinar {pickPlan?.plan_type}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-muted/40 p-3 text-sm">
              Valor: <b>{fmtBRL(Number(pickPlan?.price ?? 0))}</b> · Vigência: <b className="capitalize">{pickPlan?.validity}</b>
            </div>
            <div className="space-y-1">
              <Label>Aluno</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger><SelectValue placeholder="Selecione o aluno" /></SelectTrigger>
                <SelectContent>
                  {withoutPlan.map((s: any) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Forma de pagamento</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger><SelectValue placeholder="Escolha" /></SelectTrigger>
                <SelectContent>
                  {(pickPlan?.accepted_payment_methods ?? []).map((m: string) => (
                    <SelectItem key={m} value={m}>{paymentMethodLabel(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickPlan(null)}>Cancelar</Button>
            <Button onClick={onConfirmSubscribe} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white">
              {saving ? "Enviando…" : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal UPGRADE */}
      <Dialog open={!!upgradeFor} onOpenChange={(o) => !o && setUpgradeFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade — {upgradeFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-muted/40 p-3 text-sm space-y-1">
              <div>Plano atual: <b>{upgradeFor?._plan?.plan_type}</b> — {fmtBRL(Number(upgradeFor?._plan?.price ?? 0))}</div>
              <div className="text-xs text-muted-foreground">
                Vigência atual até <b>{fmtDate(upgradeFor?.plan_end_date)}</b>
              </div>
            </div>
            {upgradeFor && (
              <div className="rounded-md border border-amber-500 bg-amber-50 p-3 text-xs text-amber-800">
                Migração sem sobreposição: a vigência do novo plano começa em{" "}
                <b>{fmtDate(migrationStartFor(upgradeFor))}</b>, logo após o término do contrato atual.
                O pagamento do upgrade já é gerado agora.
              </div>
            )}
            <div className="space-y-1">
              <Label>Novo plano</Label>
              <Select value={upgradeTargetId} onValueChange={(v) => {
                setUpgradeTargetId(v);
                const p = plans.find((x) => x.id === v);
                setUpgradeMethod(p?.accepted_payment_methods?.[0] ?? "");
              }}>
                <SelectTrigger><SelectValue placeholder="Escolha um plano superior" /></SelectTrigger>
                <SelectContent>
                  {upgradeFor && higherPlansFor(upgradeFor).map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.plan_type} · {p.validity} · {fmtBRL(Number(p.price))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Forma de pagamento</Label>
              <Select value={upgradeMethod} onValueChange={setUpgradeMethod}>
                <SelectTrigger><SelectValue placeholder="Escolha" /></SelectTrigger>
                <SelectContent>
                  {(plans.find((p) => p.id === upgradeTargetId)?.accepted_payment_methods ?? []).map((m: string) => (
                    <SelectItem key={m} value={m}>{paymentMethodLabel(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {upgradeMethod && (
                <p className="text-xs text-muted-foreground">
                  {paymentMethodChannel(upgradeMethod) === "comprovante"
                    ? "Você receberá a chave Pix para pagar e enviar o comprovante."
                    : paymentMethodChannel(upgradeMethod) === "mercadopago"
                    ? "Pagamento online via Mercado Pago."
                    : "Combine o pagamento presencialmente no Centro."}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeFor(null)}>Cancelar</Button>
            <Button onClick={onConfirmUpgrade} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white">
              {saving ? "Enviando…" : "Confirmar upgrade"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!afterSubscribe} onOpenChange={(o) => !o && setAfterSubscribe(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Plano de pagamento confirmado</AlertDialogTitle>
            <AlertDialogDescription>
              {afterSubscribe?.allowSelfEnroll
                ? "A matrícula no plano foi realizada. Falta escolher a turma do treino. Deseja escolher agora?"
                : "A matrícula no plano foi realizada. Agora aguarde a alocação em uma turma e a confirmação da matrícula pelo centro de treinamento."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {afterSubscribe?.allowSelfEnroll ? (
              <>
                <AlertDialogCancel>Agora não</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setAfterSubscribe(null);
                    navigate({ to: "/responsavel/treinamento" as any });
                  }}
                >
                  Escolher turma
                </AlertDialogAction>
              </>
            ) : (
              <AlertDialogAction onClick={() => setAfterSubscribe(null)}>Ok, entendi</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
